import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Avatar,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SecurityIcon from "@mui/icons-material/Security";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LockIcon from "@mui/icons-material/Lock";
import { useSelector } from "react-redux";
import axios from "axios";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { WEBSERVICE, showToast } from "../../CoreDataService";

const Authorize = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { brokerBasicInfo, brokerDetailedInfo } = useSelector((state) => state);

  // Check if this is sandbox mode based on URL path
  const isSandbox = location.pathname.includes('/sandbox/authorize');

  // States for URL parameters
  const [clientId, setClientId] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [scopes, setScopes] = useState("");
  const [state, setState] = useState("");

  // Component states
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isAppEligible, setIsAppEligible] = useState(false);
  const [appDetails, setAppDetails] = useState(null);
  const [scopesDetails, setScopesDetails] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [showAllScopes, setShowAllScopes] = useState(false);

  // Theme colors - using a blue primary color
  const primaryColor = "#1a73e8";
  const exchangeName = brokerBasicInfo?.exchange || "PayBito";

  /**
   * Check if the requesting app is eligible for authorization
   */
  const checkAppEligibility = async (
    clientIdParam,
    redirectUrlParam,
    scopesParam,
    uuid
  ) => {
    const payload = {
      clientId: clientIdParam,
      redirectUrl: redirectUrlParam,
      scopes: scopesParam.split(",").map((scope) => scope.trim()),
      userUuid: uuid,
      // Add isSandbox property (1 for sandbox, 0 or undefined for regular)
      ...(isSandbox && { isSandbox: 1 })
    };

    try {
      setIsLoading(true);
      const token = localStorage.getItem("access_token");

      const response = await axios.post(
        `${WEBSERVICE}app/checkAppEligibility`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${token}`,
          },
        }
      );

      const error = response.data.error;
      setIsLoading(false);

      if (error?.error_data === 1) {
        setErrorMessage(error.error_msg || "Application verification failed");
        showToast("error", error.error_msg || "Application verification failed");
        return false;
      }

      setAppDetails(response.data.appDetails);
      setScopesDetails(response.data.appDetails?.scopes || []);
      return true;
    } catch (error) {
      setIsLoading(false);
      const errorMsg =
        error.response?.data?.error?.error_msg ||
        "Failed to verify application. Please try again.";
      setErrorMessage(errorMsg);
      showToast("error", errorMsg);
      return false;
    }
  };

  /**
   * Handle the authorization of the app
   */
  const handleAuthorizeApp = async () => {
    const payload = {
      clientId: clientId,
      redirectUrl: redirectUrl,
      scopes: scopes.split(",").map((scope) => scope.trim()),
      userUuid: localStorage.getItem("uuid"),
      state: state,
      // Add isSandbox property (1 for sandbox, 0 or undefined for regular)
      ...(isSandbox && { isSandbox: 1 })
    };

    try {
      setIsAuthorizing(true);
      const token = localStorage.getItem("access_token");

      const response = await axios.post(
        `${WEBSERVICE}app/authorizeApp`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${token}`,
          },
        }
      );

      const error = response.data.error;
      setIsAuthorizing(false);

      if (error?.error_data === 1) {
        showToast("error", error.error_msg || "Authorization failed");
        return;
      }

      clearAuthorizeParams();
      const responseRedirectUrl = response.data.redirectUrl;

      if (responseRedirectUrl) {
        showToast("success", "Authorization successful!");
        setTimeout(() => {
          window.location.href = responseRedirectUrl;
        }, 300);
      }
    } catch (error) {
      setIsAuthorizing(false);
      const errorMsg =
        error.response?.data?.error?.error_msg ||
        "Authorization failed. Please try again.";
      showToast("error", errorMsg);
    }
  };

  /**
   * Handle cancel action
   */
  const handleCancel = () => {
    clearAuthorizeParams();

    if (redirectUrl) {
      const cancelUrl = `${redirectUrl}${redirectUrl.includes("?") ? "&" : "?"}error=access_denied&error_description=User denied the authorization request`;
      window.location.href = state ? `${cancelUrl}&state=${state}` : cancelUrl;
    } else {
      navigate(isSandbox ? "/sandbox/spot" : "/spot");
    }
  };

  const clearAuthorizeParams = () => {
    localStorage.removeItem("authorize_clientId");
    localStorage.removeItem("authorize_redirectUrl");
    localStorage.removeItem("authorize_scopes");
    localStorage.removeItem("authorize_state");
  };

  const formatScopeDescription = (description) => {
    if (!description) return "";
    const formatted = description.trim().replace(/\bbroker\b/gi, "your");
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  // Get domain from redirect URL for display
  const getAppDomain = () => {
    try {
      if (redirectUrl) {
        const url = new URL(redirectUrl);
        return url.hostname;
      }
    } catch {
      return null;
    }
    return null;
  };

  // Initialize authorization on component mount
  useEffect(() => {
    const initializeAuthorization = async () => {
      let clientIdParam = searchParams.get("clientId") || "";
      let redirectUrlParam = searchParams.get("redirectUrl") || "";
      let scopesParam = searchParams.get("scopes") || "";
      let stateParam = searchParams.get("state") || "";

      // Fix URL encoding issue: + is decoded as space
      clientIdParam = clientIdParam.replace(/ /g, "+");
      redirectUrlParam = redirectUrlParam.replace(/ /g, "+");
      scopesParam = scopesParam.replace(/ /g, "+");
      stateParam = stateParam.replace(/ /g, "+");

      setClientId(clientIdParam);
      setRedirectUrl(redirectUrlParam);
      setScopes(scopesParam);
      setState(stateParam);

      if (!clientIdParam) {
        setIsLoading(false);
        setErrorMessage("Client ID is required");
        showToast("error", "Client ID is required");
        return;
      }

      if (!scopesParam) {
        setIsLoading(false);
        setErrorMessage("Scopes are required");
        showToast("error", "Scopes are required");
        return;
      }

      const token = localStorage.getItem("access_token");
      const uuid = localStorage.getItem("uuid");

      if (!token || !uuid) {
        localStorage.setItem("authorize_clientId", clientIdParam);
        localStorage.setItem("authorize_redirectUrl", redirectUrlParam);
        localStorage.setItem("authorize_scopes", scopesParam);
        if (stateParam) localStorage.setItem("authorize_state", stateParam);
        
        // Preserve sandbox mode in redirect
        showToast("info", "Please login to continue");
        navigate(isSandbox ? "/sandbox/login" : "/login");
        return;
      }

      if (clientIdParam && scopesParam) {
        const isEligible = await checkAppEligibility(
          clientIdParam,
          redirectUrlParam,
          scopesParam,
          uuid
        );
        setIsAppEligible(isEligible);
      }
    };

    initializeAuthorization();
  }, [searchParams, navigate, isSandbox]);

  // Render loading state
  const renderLoading = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "400px",
        gap: 3,
      }}
    >
      <CircularProgress size={40} sx={{ color: primaryColor }} />
      <Typography variant="body1" sx={{ color: "#5f6368 !important" }}>
        Verifying application...
      </Typography>
    </Box>
  );

  // Render error state
  const renderError = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "400px",
        textAlign: "center",
        px: 4,
      }}
    >
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          bgcolor: "#fce8e6 !important",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 40, color: "#d93025" }} />
      </Box>
      <Typography
        variant="h5"
        sx={{ color: "#202124 !important", mb: 1.5, fontWeight: 500 }}
      >
        Authorization Failed
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: "#5f6368 !important", mb: 4, maxWidth: 320 }}
      >
        {errorMessage || "We couldn't verify this application. Please try again."}
      </Typography>
      <Button
        variant="outlined"
        onClick={() => navigate(isSandbox ? "/sandbox/spot" : "/spot")}
        sx={{
          textTransform: "none",
          borderRadius: "20px",
          px: 4,
          py: 1,
          borderColor: "#dadce0",
          color: `${primaryColor} !important`,
          backgroundColor: "transparent !important",
          "&:hover": {
            borderColor: primaryColor,
            bgcolor: "rgba(26, 115, 232, 0.04) !important",
          },
        }}
      >
        Return to Home
      </Button>
    </Box>
  );

  // Render consent form
  const renderConsentForm = () => {
    const appName = appDetails?.name || "Application";
    const visibleScopes = showAllScopes ? scopesDetails : scopesDetails.slice(0, 4);
    const hasMoreScopes = scopesDetails.length > 4;

    return (
      <Box sx={{ py: 2 }}>
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          {/* App Icons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: "#ffffff !important",
                border: "2px solid #e8eaed",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              {appDetails?.icon ? (
                <Box
                  component="img"
                  src={appDetails.icon}
                  alt={appName}
                  sx={{ width: 36, height: 36, objectFit: "contain" }}
                />
              ) : (
                <SecurityIcon sx={{ fontSize: 32, color: primaryColor }} />
              )}
            </Avatar>

            {/* Connection dots */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: "#bdc1c6 !important",
                }}
              />
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: "#bdc1c6 !important",
                }}
              />
            </Box>

            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: `${primaryColor} !important`,
                boxShadow: "0 2px 8px rgba(26, 115, 232, 0.3)",
              }}
            >
              <LockIcon sx={{ fontSize: 28, color: "#ffffff" }} />
            </Avatar>
          </Box>

          {/* Title */}
          <Typography
            variant="h5"
            sx={{
              color: "#202124 !important",
              fontWeight: 400,
              mb: 1,
              fontSize: "1.375rem",
            }}
          >
            Sign in to{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {appName}
            </Box>
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: "#5f6368 !important", mb: 1.5 }}
          >
            with your {exchangeName} Account {isSandbox ? "(Sandbox)" : ""}
          </Typography>

          {getAppDomain() && (
            <Chip
              label={getAppDomain()}
              size="small"
              sx={{
                bgcolor: "#e8f0fe !important",
                color: `${primaryColor} !important`,
                fontSize: "12px",
                fontWeight: 500,
                height: "26px",
                border: "none",
                "& .MuiChip-label": {
                  color: `${primaryColor} !important`,
                },
              }}
            />
          )}
        </Box>

        <Divider sx={{ borderColor: "#e8eaed !important", mb: 3 }} />

        {/* Permissions Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "#202124 !important",
              fontWeight: 600,
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: "0.875rem",
            }}
          >
            <SecurityIcon sx={{ fontSize: 18, color: "#5f6368" }} />
            {appName} will be able to:
          </Typography>

          <Box
            sx={{
              bgcolor: "#f8f9fa !important",
              borderRadius: "12px",
              overflow: "hidden",
              border: "1px solid #e8eaed",
            }}
          >
            {visibleScopes.map((scope, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  px: 2.5,
                  py: 1.75,
                  bgcolor: "#ffffff !important",
                  borderBottom:
                    index < visibleScopes.length - 1
                      ? "1px solid #f1f3f4"
                      : "none",
                }}
              >
                <CheckCircleIcon
                  sx={{
                    fontSize: 20,
                    color: "#34a853",
                    mt: 0.25,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: "#3c4043 !important",
                    lineHeight: 1.5,
                    fontSize: "0.875rem",
                  }}
                >
                  {formatScopeDescription(scope.scopeDescription)}
                </Typography>
              </Box>
            ))}

            {/* Show More/Less Button */}
            {hasMoreScopes && (
              <Box
                onClick={() => setShowAllScopes(!showAllScopes)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  py: 1.5,
                  cursor: "pointer",
                  bgcolor: "#f8f9fa !important",
                  borderTop: "1px solid #e8eaed",
                  color: `${primaryColor} !important`,
                  transition: "background-color 0.2s",
                  "&:hover": {
                    bgcolor: "#e8f0fe !important",
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: `${primaryColor} !important` }}
                >
                  {showAllScopes
                    ? "Show less"
                    : `Show ${scopesDetails.length - 4} more permissions`}
                </Typography>
                {showAllScopes ? (
                  <ExpandLessIcon sx={{ fontSize: 20, color: primaryColor }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 20, color: primaryColor }} />
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Warning Notice */}
        <Box
          sx={{
            bgcolor: "#fef7e0 !important",
            borderRadius: "12px",
            p: 2.5,
            mb: 4,
            display: "flex",
            gap: 2,
            border: "1px solid #fde293",
          }}
        >
          <ErrorOutlineIcon
            sx={{ fontSize: 22, color: "#f9ab00", flexShrink: 0, mt: 0.25 }}
          />
          <Typography
            variant="body2"
            sx={{ color: "#5f6368 !important", lineHeight: 1.7, fontSize: "0.8125rem" }}
          >
            Make sure you trust{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "#202124 !important" }}>
              {appName}
            </Box>
            . You may be sharing sensitive info with this app. You can revoke
            access at any time in your account settings.
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
          }}
        >
          <Button
            variant="text"
            onClick={handleCancel}
            disabled={isAuthorizing}
            sx={{
              textTransform: "none",
              color: `${primaryColor} !important`,
              fontWeight: 600,
              px: 3,
              py: 1.25,
              borderRadius: "24px",
              fontSize: "0.875rem",
              backgroundColor: "transparent !important",
              "&:hover": {
                bgcolor: "rgba(26, 115, 232, 0.08) !important",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAuthorizeApp}
            disabled={isAuthorizing}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 4,
              py: 1.25,
              borderRadius: "24px",
              bgcolor: `${primaryColor} !important`,
              color: "#ffffff !important",
              fontSize: "0.875rem",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
              "&:hover": {
                bgcolor: "#1557b0 !important",
                boxShadow: "0 2px 6px rgba(0,0,0,0.2), 0 2px 4px rgba(0,0,0,0.16)",
              },
              "&:disabled": {
                bgcolor: "#dadce0 !important",
                color: "#80868b !important",
              },
            }}
          >
            {isAuthorizing ? (
              <CircularProgress size={20} sx={{ color: "#ffffff" }} />
            ) : (
              "Allow"
            )}
          </Button>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Typography
            variant="caption"
            sx={{ color: "#9aa0a6 !important", lineHeight: 1.6 }}
          >
            By clicking Allow, you authorize this app to use your information in
            accordance with their terms of service and privacy policy.
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f0f4f9 !important",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        px: 2,
        position: 'relative',
      }}
    >
      {/* Sandbox indicator badge */}
      {isSandbox && (
        <Chip
          label="SANDBOX MODE"
          size="small"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            bgcolor: '#ff9800 !important',
            color: '#000000 !important',
            fontWeight: 'bold',
            fontSize: '11px',
            height: '24px',
            zIndex: 10,
            '& .MuiChip-label': {
              color: '#000000 !important',
            },
          }}
        />
      )}
      
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 480,
          borderRadius: "24px",
          border: isSandbox ? "2px solid #ff9800" : "1px solid #dadce0",
          overflow: "hidden",
          bgcolor: "#ffffff !important",
          boxShadow: isSandbox 
            ? "0 1px 3px rgba(255, 152, 0, 0.2), 0 4px 12px rgba(255, 152, 0, 0.1)"
            : "0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.05)",
        }}
      >
        {/* Top Bar with Logo and Mode Indicator */}
        <Box
          sx={{
            px: 4,
            py: 2.5,
            borderBottom: "1px solid #f1f3f4",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#ffffff !important",
          }}
        >
          {localStorage.getItem("exchangeLogo") ? (
            <Box
              component="img"
              src={localStorage.getItem("exchangeLogo")}
              alt={exchangeName}
              sx={{ height: 32, width: "auto" }}
            />
          ) : (
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#202124 !important",
                letterSpacing: "-0.5px",
                fontSize: "1.25rem",
              }}
            >
              {exchangeName}
            </Typography>
          )}
          
          {isSandbox && (
            <Typography
              variant="caption"
              sx={{
                color: "#ff9800 !important",
                fontWeight: 600,
                bgcolor: "#fff3e0 !important",
                px: 1.5,
                py: 0.5,
                borderRadius: "12px",
                fontSize: "0.7rem",
              }}
            >
              SANDBOX
            </Typography>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ px: 4, py: 3, bgcolor: "#ffffff !important" }}>
          {isLoading
            ? renderLoading()
            : isAppEligible && scopes
              ? renderConsentForm()
              : renderError()}
        </Box>
      </Paper>
    </Box>
  );
};

export default Authorize;